import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Loader2, Pencil, Trash2 } from 'lucide-react';
import { typography } from '../../styles/typography';
import { Card, PrimaryButton, SearchBar } from '../ui';
import {
  LOST_FOUND_STATUS,
  LOST_FOUND_TABS,
  formatLostFoundDate,
  getStatusLabel,
  tabToListFilter,
} from './lostAndFoundData';
import { lostAndFoundApi } from './lostAndFoundApi';
import useLostFoundSite, { resolveSiteLabel, resolveSiteValue } from './useLostFoundSite';

const STATUS_STYLES = {
  [LOST_FOUND_STATUS.LOST]: 'bg-amber-50 text-amber-800 border-amber-200',
  [LOST_FOUND_STATUS.FOUND]: 'bg-blue-50 text-blue-800 border-blue-200',
  [LOST_FOUND_STATUS.COLLECTED]: 'bg-green-50 text-green-800 border-green-200',
};

const SITE_SELECT_CLASS =
  'w-full max-w-md rounded-xl border border-zinc-300 bg-white px-4 py-3 text-base text-black font-["Open_Sans"] focus:outline-none focus:ring-2 focus:ring-[#6BABA0]/40 focus:border-[#6BABA0]';

const ActionIconButton = ({ icon: Icon, label, onClick, disabled, colorClass, spinIcon }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className={`flex h-9 w-9 items-center justify-center rounded-lg text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 ${colorClass}`}
  >
    <Icon className={`w-4 h-4 ${spinIcon ? 'animate-spin' : ''}`} aria-hidden />
  </button>
);

const LostAndFound = () => {
  const navigate = useNavigate();
  const { siteId, sites, isLoadingSites, sitesError, selectSite, hasSite } =
    useLostFoundSite();
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const loadItems = useCallback(async () => {
    if (!hasSite) {
      setItems([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const { items: data } = await lostAndFoundApi.getAll({
        siteId,
        statusFilter: tabToListFilter(activeTab),
        search: searchTerm,
      });
      setItems(data);
    } catch (err) {
      setError(err.message || 'Failed to load items');
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [hasSite, siteId, activeTab, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadItems();
    }, searchTerm ? 350 : 0);
    return () => clearTimeout(timer);
  }, [loadItems, searchTerm]);

  const filteredItems = items;

  const handleView = (e, itemId) => {
    e.stopPropagation();
    navigate(`/lost-and-found/${itemId}`);
  };

  const handleEdit = (e, itemId) => {
    e.stopPropagation();
    navigate(`/lost-and-found/${itemId}/edit`);
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      `Delete "${item.item_name || 'this item'}"? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(item.id);
    try {
      await lostAndFoundApi.delete(item.id, siteId);
      await loadItems();
    } catch (err) {
      alert(err.message || 'Failed to delete item');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className={typography.h3} style={{ color: '#000000' }}>
          Lost & Found
        </h3>
        <PrimaryButton
          onClick={() => navigate('/lost-and-found/create')}
          disabled={!hasSite}
          className="!w-auto min-w-[200px] !h-12 !text-base"
        >
          Create Lost & Found
        </PrimaryButton>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="lost-found-site"
          className="text-sm font-medium text-black font-['Open_Sans']"
        >
          Site
        </label>
        {isLoadingSites ? (
          <p className="text-sm text-gray-500 font-['Open_Sans']">Loading sites…</p>
        ) : sitesError ? (
          <p className="text-sm text-red-600 font-['Open_Sans']">{sitesError}</p>
        ) : (
          <select
            id="lost-found-site"
            value={siteId}
            onChange={e => selectSite(e.target.value)}
            className={SITE_SELECT_CLASS}
          >
            <option value="">Select a site</option>
            {sites.map(site => {
              const value = resolveSiteValue(site);
              return (
                <option key={value} value={value}>
                  {resolveSiteLabel(site)}
                </option>
              );
            })}
          </select>
        )}
        {!hasSite && !isLoadingSites && (
          <p className="text-xs text-gray-500 font-['Open_Sans']">
            Select a site to load community lost & found items.
          </p>
        )}
      </div>

      <div className="flex border-b border-zinc-200 gap-1">
        {LOST_FOUND_TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            disabled={!hasSite}
            className={`px-4 py-2.5 text-sm font-['Open_Sans'] border-b-2 transition-colors -mb-px disabled:opacity-50 ${
              activeTab === tab.id
                ? 'border-[#6BABA0] text-black font-semibold'
                : 'border-transparent text-gray-500 hover:text-black'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <SearchBar
        placeholder="search item name"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        iconType="search"
        className="w-full max-w-2xl"
        disabled={!hasSite}
      />

      <Card className="overflow-hidden p-0">
        {!hasSite ? (
          <div className="text-center py-16 px-4 text-gray-500 font-['Open_Sans']">
            Select a site to view items
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-[#6BABA0] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12 px-4">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              type="button"
              onClick={loadItems}
              className="px-4 py-2 rounded-lg bg-[#6BABA0] text-white hover:opacity-90"
            >
              Retry
            </button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16 px-4 text-gray-500 font-['Open_Sans']">
            No items found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-zinc-200 bg-gray-50/80">
                  {['Item', 'Image', 'Status', 'Location', 'Date', 'Actions'].map(col => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-700 font-['Open_Sans']"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => (
                  <tr
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/lost-and-found/${item.id}`)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/lost-and-found/${item.id}`);
                      }
                    }}
                    className="border-b border-zinc-100 hover:bg-gray-50/50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="text-base font-medium text-black font-['Open_Sans']">
                        {item.item_name}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.item_name}
                          className="w-12 h-12 rounded-lg border border-zinc-200 object-cover"
                        />
                      ) : (
                        <span className="text-sm text-gray-400 font-['Open_Sans']">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium border font-['Open_Sans'] ${
                          STATUS_STYLES[item.status] || 'bg-gray-50 text-gray-700 border-gray-200'
                        }`}
                      >
                        {getStatusLabel(item.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-base text-black font-['Open_Sans']">
                      {item.location || '—'}
                    </td>
                    <td className="px-4 py-3 text-base text-black font-['Open_Sans'] whitespace-nowrap">
                      {formatLostFoundDate(item.date)}
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex flex-row items-center gap-2">
                        <ActionIconButton
                          icon={Eye}
                          label="View"
                          onClick={e => handleView(e, item.id)}
                          colorClass="bg-[#6BABA0] hover:bg-[#5a9a90]"
                        />
                        {item.status !== LOST_FOUND_STATUS.COLLECTED && (
                          <ActionIconButton
                            icon={Pencil}
                            label="Edit"
                            onClick={e => handleEdit(e, item.id)}
                            colorClass="bg-blue-600 hover:bg-blue-700"
                          />
                        )}
                        <ActionIconButton
                          icon={deletingId === item.id ? Loader2 : Trash2}
                          label={deletingId === item.id ? 'Deleting' : 'Delete'}
                          onClick={e => handleDelete(e, item)}
                          disabled={deletingId === item.id}
                          spinIcon={deletingId === item.id}
                          colorClass="bg-red-600 hover:bg-red-700"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default LostAndFound;
